/**
 * Country Service
 * Handles country data from REST Countries API
 */

// Cache for country data
let countriesCache = null;
const CACHE_KEY = 'countries_cache';
const CACHE_TIMESTAMP_KEY = 'countries_cache_timestamp';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

const countryService = {
  /**
   * Get all countries from REST Countries API (with localStorage cache)
   * @returns {Promise<Array>} Array of country objects
   */
  async getAllCountries() {
    // Return memory cache if available
    if (countriesCache) {
      return countriesCache;
    }

    try {
      // Check localStorage cache
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cachedData && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        
        // If cache is fresh (less than 24 hours old), use it
        if (age < CACHE_DURATION) {
          console.log('Using cached country data (age:', Math.round(age / 1000 / 60), 'minutes)');
          countriesCache = JSON.parse(cachedData);
          return countriesCache;
        }
      }

      // Fetch from API
      console.log('Fetching country data from API...');
      const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca3,latlng');
      const data = await response.json();
      
      // Transform to our format
      countriesCache = data.map(country => ({
        code: country.cca3, // ISO 3166-1 alpha-3
        name: country.name.common,
        coordinates: country.latlng ? [country.latlng[1], country.latlng[0]] : [0, 0] // [lng, lat] for Mapbox
      }));
      
      // Save to localStorage
      localStorage.setItem(CACHE_KEY, JSON.stringify(countriesCache));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      console.log('Country data cached');
      
      return countriesCache;
    } catch (error) {
      console.error('Error fetching countries:', error);
      
      // Fallback to expired cache if available
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        console.log('Using expired cache as fallback');
        countriesCache = JSON.parse(cachedData);
        return countriesCache;
      }
      
      return [];
    }
  },

  /**
   * Get country coordinates by code
   * @param {string} code - ISO 3166-1 alpha-3 code
   * @returns {Promise<Array>} [lng, lat]
   */
  async getCountryCoordinates(code) {
    const countries = await this.getAllCountries();
    const country = countries.find(c => c.code === code);
    return country ? country.coordinates : [0, 0];
  },

  /**
   * Get country name by code
   * @param {string} code - ISO 3166-1 alpha-3 code
   * @returns {Promise<string>} Country name
   */
  async getCountryName(code) {
    const countries = await this.getAllCountries();
    const country = countries.find(c => c.code === code);
    return country ? country.name : code;
  },

  /**
   * Get country coordinates map (code -> [lng, lat])
   * @returns {Promise<Object>} Object with country codes as keys
   */
  async getCountryCoordinatesMap() {
    const countries = await this.getAllCountries();
    const map = {};
    
    countries.forEach(country => {
      map[country.code] = country.coordinates;
    });
    
    // Add some common aliases
    map['Local'] = [0, 0];
    map['Unknown'] = [0, 0];
    
    return map;
  },

  /**
   * Get country names map (code -> name)
   * @returns {Promise<Object>} Object with country codes as keys
   */
  async getCountryNamesMap() {
    const countries = await this.getAllCountries();
    const map = {};
    
    countries.forEach(country => {
      map[country.code] = country.name;
    });
    
    return map;
  },

  /**
   * Clear cache (useful for refresh)
   */
  clearCache() {
    countriesCache = null;
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    console.log('Country cache cleared');
  }
};

export default countryService;
